import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX } = FiIcons;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Optimized logo with auto format and quality
  const logoUrl = "https://res.cloudinary.com/digw3ykex/image/upload/f_auto,q_auto/v1767262722/LIVING_MEDIA_LOGO_TRANSPARENT_lvtpzg.png";

  const navItems = ['Services', 'Process', 'Results', 'Pricing'];

  const handleNavClick = (e, item) => {
    const sectionId = item.toLowerCase();
    if (isHome) {
      e.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMenuOpen(false);
        // Update URL hash without reload for linking consistency
        window.history.pushState(null, '', `/#/#${sectionId}`);
      }
    } else {
      setIsMenuOpen(false);
    }
  };

  const handleInquireClick = (e) => {
    if (isHome) {
      e.preventDefault();
      const element = document.getElementById('start-calibration');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMenuOpen(false);
        window.history.pushState(null, '', `/#/#start-calibration`);
      }
    } else {
      setIsMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${isScrolled ? 'py-3 bg-black/90 backdrop-blur-md border-b border-white/10' : 'py-6 md:py-8 bg-transparent'}`}>
      <div className="max-w-[1400px] mx-auto px-6 flex justify-between items-center">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          className="flex items-center"
        >
          <Link 
            to="/" 
            onClick={() => isHome && window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Living Media Homepage"
          >
            <img 
              src={logoUrl} 
              alt="LIVING MEDIA" 
              // LCP Optimization: High priority fetch, no lazy loading
              fetchPriority="high"
              width="300" 
              height="150"
              className={`w-auto object-contain transition-all duration-500 ${isScrolled ? 'h-20 md:h-32' : 'h-28 md:h-48'}`} 
            />
          </Link>
        </motion.div>

        <nav className="hidden md:flex items-center space-x-12" role="navigation" aria-label="Main Navigation">
          {navItems.map((item) => (
            <Link 
              key={item} 
              to={{ pathname: '/', hash: `#${item.toLowerCase()}` }}
              onClick={(e) => handleNavClick(e, item)}
              className="text-sm font-bold uppercase tracking-widest text-white hover:text-[#BEF264] transition-colors"
              aria-label={`Navigate to ${item} section`}
            >
              {item}
            </Link>
          ))}
          <Link 
            to={{ pathname: '/', hash: '#start-calibration' }}
            onClick={handleInquireClick}
            className="bg-[#BEF264] text-black px-6 py-2 rounded-full text-sm font-black uppercase tracking-widest hover:bg-white transition-all transform hover:scale-105"
            aria-label="Inquire about Video Editing Services"
          >
            Inquire
          </Link>
        </nav>

        <button 
          className="md:hidden text-white p-2" 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle Mobile Menu"
        >
          <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-7 h-7" />
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }} 
          animate={{ opacity: 1, height: 'auto' }}
          className="absolute top-full left-0 w-full bg-black border-b border-white/10 p-8 md:hidden overflow-hidden"
        >
          <div className="flex flex-col space-y-8">
            {navItems.map((item) => (
              <Link 
                key={item} 
                to={{ pathname: '/', hash: `#${item.toLowerCase()}` }}
                onClick={(e) => handleNavClick(e, item)}
                className="text-3xl font-black uppercase text-white tracking-tighter"
              >
                {item}
              </Link>
            ))}
            <Link 
              to={{ pathname: '/', hash: '#start-calibration' }}
              onClick={handleInquireClick}
              className="bg-[#BEF264] text-black p-5 text-center font-black uppercase rounded-2xl tracking-widest"
            >
              Get Started
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header;